import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
export interface BlogPostMeta {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  imageAlt: string;
  readTime: string;
  category: string;
}
export const blogPosts: BlogPostMeta[] = [{
  slug: 'beyond-the-calorie-count',
  title: "Beyond the Calorie Count: Why Your Food's Ingredients Dictate Your Health",
  excerpt: 'It is easy to look at food purely as energy. But your body is a complex system, and the quality of the fuel you put into it matters just as much as the quantity.',
  image: 'https://cdn.magicpatterns.com/uploads/8GxZMYZ31JXPcUyM5pYVaA/myimg.png',
  imageAlt: 'A bowl of healthy whole foods including avocado, quinoa, berries, spinach, and olive oil',
  readTime: '5 min read',
  category: 'Nutrition'
}, {
  slug: 'healthy-food-trap',
  title: 'The Healthy Food Trap: 7 Packaging Claims That Can Fool Smart Shoppers',
  excerpt: 'Products labeled "Natural," "Multigrain," and "Sugar-Free" sound healthy, but they don\'t always tell the full story. Learn what these claims really mean.',
  image: 'https://cdn.magicpatterns.com/uploads/5oWwN3D7ZzjwLcFQux3Vrf/image_ing.png',
  imageAlt: 'Infographic comparing common food marketing claims versus the reality on the ingredient label',
  readTime: '6 min read',
  category: 'Smart Shopping'
}];
export function getPostMeta(slug: string): BlogPostMeta | undefined {
  return blogPosts.find(p => p.slug === slug);
}
// ---------- Article Bodies ----------
export function BeyondCalorieCountBody() {
  return <div className="prose prose-lg prose-slate max-w-none" data-id="element-56">
      <p className="text-xl text-slate-600 leading-relaxed mb-10 font-medium" data-id="element-57">
        We make dozens of choices every day, but few have a more immediate and
        compounding impact on our physical well-being than what we choose to
        eat. It is easy to look at food purely as energy—a simple math equation
        of calories in versus calories out. However, your body is a highly
        complex system, and the quality of the fuel you put into it matters just
        as much, if not more, than the quantity.
      </p>

      <figure className="my-12" data-id="element-58">
        <img src="https://cdn.magicpatterns.com/uploads/8GxZMYZ31JXPcUyM5pYVaA/myimg.png" alt="A bowl of healthy whole foods including avocado, quinoa, berries, spinach, and olive oil" className="w-full rounded-2xl shadow-xl object-cover aspect-[16/9]" data-id="element-59" />
        <figcaption className="text-center text-sm text-slate-500 mt-4" data-id="element-60">
          Whole, single-ingredient foods provide the essential building blocks
          your body needs.
        </figcaption>
      </figure>

      <p className="mb-12 text-slate-700 leading-relaxed" data-id="element-61">
        When you flip a package over to read the ingredient list, you are
        looking at the literal building blocks your body will use to repair
        cells, power your brain, and regulate your mood. Here is a breakdown of
        why the specific ingredients in your food are the true drivers of your
        physical health.
      </p>

      <div className="mb-16" data-id="element-62">
        <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center" data-id="element-63">
          <span className="bg-brand-100 text-brand-600 p-2 rounded-xl mr-4" data-id="element-64">
            <CheckCircle2 className="w-6 h-6" data-id="element-65" />
          </span>
          The "Good" Ingredients: Your Body's Operational Toolkit
        </h2>
        <p className="mb-8 text-slate-700" data-id="element-66">
          Nutrient-dense, whole-food ingredients are not just "healthy"—they are
          functional. They provide the necessary materials for your body to
          execute millions of chemical reactions every second.
        </p>

        <div className="space-y-6" data-id="element-67">
          <div className="bg-brand-50/50 border border-brand-100 rounded-2xl p-6" data-id="element-68">
            <h3 className="text-lg font-bold mb-2 text-brand-800" data-id="element-69">
              Complex Carbohydrates (Oats, Quinoa, Sweet Potatoes)
            </h3>
            <p className="text-slate-700 m-0" data-id="element-70">
              Unlike simple sugars that spike your blood glucose, complex carbs
              contain fiber. Fiber slows down digestion, providing a steady,
              sustainable release of energy while keeping your gut microbiome
              balanced.
            </p>
          </div>
          <div className="bg-brand-50/50 border border-brand-100 rounded-2xl p-6" data-id="element-71">
            <h3 className="text-lg font-bold mb-2 text-brand-800" data-id="element-72">
              Lean Proteins (Chicken, Fish, Tofu, Legumes)
            </h3>
            <p className="text-slate-700 m-0" data-id="element-73">
              Dietary protein is broken down into amino acids. These are the
              primary structural components for your muscles, skin, enzymes, and
              hormones. High-quality protein ensures your body can repair itself
              after physical exertion or stress.
            </p>
          </div>
          <div className="bg-brand-50/50 border border-brand-100 rounded-2xl p-6" data-id="element-74">
            <h3 className="text-lg font-bold mb-2 text-brand-800" data-id="element-75">
              Healthy Fats (Avocados, Olive Oil, Nuts, Seeds)
            </h3>
            <p className="text-slate-700 m-0" data-id="element-76">
              Fats are critical for brain health and the absorption of
              fat-soluble vitamins (like Vitamins A, D, E, and K). Omega-3 fatty
              acids, in particular, actively reduce cellular inflammation.
            </p>
          </div>
          <div className="bg-brand-50/50 border border-brand-100 rounded-2xl p-6" data-id="element-77">
            <h3 className="text-lg font-bold mb-2 text-brand-800" data-id="element-78">
              Vitamins and Minerals (Leafy Greens, Berries, Cruciferous Veggies)
            </h3>
            <p className="text-slate-700 m-0" data-id="element-79">
              These act as catalysts. For example, magnesium aids in muscle
              function and sleep, while vitamin C is essential for immune
              response and collagen synthesis.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-16" data-id="element-80">
        <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center" data-id="element-81">
          <span className="bg-red-100 text-red-600 p-2 rounded-xl mr-4" data-id="element-82">
            <XCircle className="w-6 h-6" data-id="element-83" />
          </span>
          The "Bad" Ingredients: The System Disruptors
        </h2>
        <p className="mb-8 text-slate-700" data-id="element-84">
          When we talk about "bad" ingredients, we are generally referring to
          highly processed additives that provide zero nutritional value and,
          over time, disrupt the body's natural homeostasis.
        </p>

        <div className="space-y-6" data-id="element-85">
          <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6" data-id="element-86">
            <h3 className="text-lg font-bold mb-2 text-red-800" data-id="element-87">
              Added Sugars and High-Fructose Corn Syrup
            </h3>
            <p className="text-slate-700 m-0" data-id="element-88">
              These are pervasive in processed foods. Excessive sugar intake
              overwhelms the liver, spikes insulin levels, and drives systemic
              inflammation. Over time, this constant spiking can lead to insulin
              resistance, a precursor to type 2 diabetes and metabolic syndrome.
            </p>
          </div>
          <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6" data-id="element-89">
            <h3 className="text-lg font-bold mb-2 text-red-800" data-id="element-90">
              Trans Fats and Partially Hydrogenated Oils
            </h3>
            <p className="text-slate-700 m-0" data-id="element-91">
              Artificial trans fats are incredibly taxing on the cardiovascular
              system. They actively raise LDL ("bad") cholesterol while lowering
              HDL ("good") cholesterol, increasing the risk of heart disease.
            </p>
          </div>
          <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6" data-id="element-92">
            <h3 className="text-lg font-bold mb-2 text-red-800" data-id="element-93">
              Excessive Sodium
            </h3>
            <p className="text-slate-700 m-0" data-id="element-94">
              While sodium is an essential electrolyte, the massive quantities
              found in ultra-processed foods force the heart to work harder to
              pump blood, which can lead to chronic high blood pressure and
              vascular damage.
            </p>
          </div>
          <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6" data-id="element-95">
            <h3 className="text-lg font-bold mb-2 text-red-800" data-id="element-96">
              Artificial Preservatives and Emulsifiers
            </h3>
            <p className="text-slate-700 m-0" data-id="element-97">
              While recognized as safe in small quantities, emerging research
              suggests that a heavy reliance on heavily processed chemical
              additives can alter gut bacteria and irritate the intestinal
              lining.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-16" data-id="element-98">
        <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-99">
          The Cumulative Effect
        </h2>
        <p className="text-slate-700 mb-4" data-id="element-100">
          One meal will not make or break your health. The danger of poor
          ingredients lies in the cumulative effect. Consistently eating foods
          stripped of their nutrients and packed with inflammatory additives
          leaves the body overfed but undernourished. This is often the root
          cause of chronic fatigue, brain fog, poor sleep, and a weakened immune
          system.
        </p>
        <p className="text-slate-700 font-medium p-6 bg-slate-50 rounded-2xl border border-slate-100 italic" data-id="element-101">
          Conversely, consistently choosing whole, single-ingredient foods gives
          your body the exact tools it needs to thrive, repair, and maintain
          high energy levels.
        </p>
      </div>

      <div className="bg-gradient-to-br from-brand-50 to-accent-50 rounded-3xl p-8 sm:p-10 border border-brand-100" data-id="element-102">
        <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-103">
          Take Action Today
        </h2>
        <p className="text-slate-700 mb-8" data-id="element-104">
          The transition to healthier eating doesn't require a flawless diet; it
          requires awareness.
        </p>
        <ul className="space-y-4 list-none p-0 m-0" data-id="element-105">
          <li className="flex items-start" data-id="element-106">
            <div className="bg-brand-600 text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-1 mr-4 text-sm font-bold" data-id="element-107">
              1
            </div>
            <div data-id="element-108">
              <strong className="text-slate-900 block mb-1" data-id="element-109">
                Use IntelliEats to Understand the Label
              </strong>
              <span className="text-slate-700" data-id="element-110">
                If the ingredient list reads like a chemistry experiment, your
                body will likely struggle to process it.
              </span>
            </div>
          </li>
          <li className="flex items-start" data-id="element-111">
            <div className="bg-brand-600 text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-1 mr-4 text-sm font-bold" data-id="element-112">
              2
            </div>
            <div data-id="element-113">
              <strong className="text-slate-900 block mb-1" data-id="element-114">
                Prioritize Single-Ingredient Foods
              </strong>
              <span className="text-slate-700" data-id="element-115">
                An apple has one ingredient: an apple.
              </span>
            </div>
          </li>
          <li className="flex items-start" data-id="element-116">
            <div className="bg-brand-600 text-white rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-1 mr-4 text-sm font-bold" data-id="element-117">
              3
            </div>
            <div data-id="element-118">
              <strong className="text-slate-900 block mb-1" data-id="element-119">
                Cook at Home
              </strong>
              <span className="text-slate-700" data-id="element-120">
                It is the only way to have 100% control over the oils, sugars,
                and sodium entering your meals.
              </span>
            </div>
          </li>
        </ul>
        <div className="mt-10 pt-8 border-t border-brand-200/50" data-id="element-121">
          <p className="text-lg font-bold text-slate-900 text-center" data-id="element-122">
            Your health is an ongoing investment, and every meal is an
            opportunity to make a deposit.
          </p>
        </div>
      </div>
    </div>;
}
const claims = [{
  n: '1',
  title: '"Made With Real Fruit"',
  body: 'This phrase often creates the impression that a product is packed with nutritious fruit. The reality? A snack may contain a small amount of fruit concentrate while most of its sweetness comes from added sugars, syrups, or artificial flavors.',
  check: ['Is real fruit listed near the beginning of the ingredient list?', 'How many forms of added sugar are included?', 'Does the product contain artificial colors or flavors?']
}, {
  n: '2',
  title: '"Natural"',
  body: 'Many shoppers assume "natural" means healthy. Unfortunately, the term is often loosely used. A product can be highly processed and still contain ingredients marketed as natural.',
  check: ['Length of the ingredient list', 'Artificial additives', 'Preservatives and flavor enhancers', 'Amount of added sugar'],
  note: 'A product labeled "natural" isn\'t automatically nutritious.'
}, {
  n: '3',
  title: '"Multigrain"',
  body: 'Multigrain simply means multiple grains were used. It does not guarantee that those grains are whole grains. Many multigrain products still contain refined flour as the primary ingredient.',
  check: ['Whole wheat', 'Whole oats', 'Whole grain corn', 'Brown rice (as the first ingredients)']
}, {
  n: '4',
  title: '"Sugar-Free"',
  body: 'Sugar-free products often replace sugar with artificial sweeteners or sugar alcohols. While these alternatives can reduce calories, they may not be the best option for everyone.',
  check: ['Aspartame', 'Sucralose', 'Saccharin', 'Acesulfame Potassium'],
  note: 'Understanding what replaces the sugar is just as important as knowing how much sugar is removed.'
}, {
  n: '5',
  title: '"Low Fat"',
  body: 'When manufacturers reduce fat, they often compensate by adding sugar, starches, or flavoring agents to improve taste. A low-fat label does not necessarily mean healthier.',
  check: ['Added sugars', 'Total ingredients', 'Sodium levels'],
  note: 'Sometimes the regular version contains fewer additives.'
}, {
  n: '6',
  title: '"Made With Whole Grains"',
  body: "This claim sounds impressive, but it doesn't tell you how much whole grain is actually present. A product may contain a small amount of whole grain while still being mostly refined flour.",
  check: ['Read the ingredient order. Ingredients are listed from highest to lowest quantity.']
}, {
  n: '7',
  title: '"No Artificial Colors"',
  body: "This is a positive claim, but it doesn't automatically make a product healthy. A food can be free of artificial colors and still contain excess sugar, refined carbohydrates, highly processed oils, or large amounts of sodium.",
  check: ['Always evaluate the entire ingredient list rather than a single marketing claim.']
}];
export function HealthyFoodTrapBody() {
  return <div className="prose prose-lg prose-slate max-w-none" data-id="element-123">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-500 italic mb-10" data-id="element-124">
        Disclaimer: This article is intended for educational purposes only and
        should not be considered medical or dietary advice. Always consult a
        qualified healthcare professional for personalized nutrition
        recommendations.
      </div>

      <p className="text-xl text-slate-600 leading-relaxed mb-10 font-medium" data-id="element-125">
        Walk through any grocery store and you'll see products labeled
        "Natural," "Made with Real Fruit," "Multigrain," "Sugar-Free," and "Low
        Fat." These claims sound healthy, but they don't always tell the full
        story.
      </p>

      <figure className="my-12" data-id="element-126">
        <img src="https://cdn.magicpatterns.com/uploads/5oWwN3D7ZzjwLcFQux3Vrf/image_ing.png" alt="Infographic comparing common food marketing claims versus the reality on the ingredient label" className="w-full rounded-2xl shadow-xl" data-id="element-127" />
        <figcaption className="text-center text-sm text-slate-500 mt-4" data-id="element-128">
          Marketing claims vs. reality — the front of the package rarely tells
          the full story.
        </figcaption>
      </figure>

      <p className="mb-12 text-slate-700 leading-relaxed" data-id="element-129">
        Many consumers focus on the front of the package while the real story is
        hidden on the ingredient label. Understanding what these marketing terms
        actually mean can help you make smarter food choices for yourself and
        your family.
      </p>

      <div className="space-y-6 mb-16" data-id="element-130">
        {claims.map(claim => <div key={claim.n} className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6" data-id="element-131">
            <h2 className="text-2xl font-bold text-slate-900 mb-3 flex items-center" data-id="element-132">
              <span className="bg-accent-100 text-accent-700 rounded-xl w-9 h-9 flex items-center justify-center mr-3 text-base shrink-0" data-id="element-133">
                {claim.n}
              </span>
              {claim.title}
            </h2>
            <p className="text-slate-700 mb-4" data-id="element-134">{claim.body}</p>
            <div className="bg-brand-50/60 border border-brand-100 rounded-xl p-4" data-id="element-135">
              <p className="text-sm font-bold text-brand-800 m-0 mb-2" data-id="element-136">
                What to check:
              </p>
              <ul className="space-y-1.5 list-none p-0 m-0" data-id="element-137">
                {claim.check.map((item, i) => <li key={i} className="flex items-start gap-2 text-slate-700 text-sm" data-id="element-138">
                    <CheckCircle2 className="w-4 h-4 text-brand-600 shrink-0 mt-0.5" data-id="element-139" />
                    <span data-id="element-140">{item}</span>
                  </li>)}
              </ul>
            </div>
            {claim.note && <p className="text-sm text-slate-500 italic mt-3 mb-0" data-id="element-141">
                {claim.note}
              </p>}
          </div>)}
      </div>

      <div className="bg-gradient-to-br from-brand-50 to-accent-50 rounded-3xl p-8 sm:p-10 border border-brand-100 mb-16" data-id="element-142">
        <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-143">
          A Simple Rule for Smarter Shopping
        </h2>
        <p className="text-slate-700 mb-2" data-id="element-144">
          When evaluating packaged foods, don't start with the front of the
          package.
        </p>
        <p className="text-slate-900 font-bold mb-6" data-id="element-145">
          Start with the ingredient list.
        </p>
        <p className="text-slate-700 mb-4" data-id="element-146">Ask yourself:</p>
        <ul className="space-y-3 list-none p-0 m-0" data-id="element-147">
          <li className="flex items-start gap-2 text-slate-700" data-id="element-148">
            <CheckCircle2 className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" data-id="element-149" />
            <span data-id="element-150">Can I recognize most of these ingredients?</span>
          </li>
          <li className="flex items-start gap-2 text-slate-700" data-id="element-151">
            <CheckCircle2 className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" data-id="element-152" />
            <span data-id="element-153">
              Are added sugars listed multiple times under different names?
            </span>
          </li>
          <li className="flex items-start gap-2 text-slate-700" data-id="element-154">
            <CheckCircle2 className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" data-id="element-155" />
            <span data-id="element-156">
              Are artificial colors, preservatives, or flavor enhancers present?
            </span>
          </li>
          <li className="flex items-start gap-2 text-slate-700" data-id="element-157">
            <CheckCircle2 className="w-5 h-5 text-brand-600 shrink-0 mt-0.5" data-id="element-158" />
            <span data-id="element-159">
              Is the ingredient list surprisingly long for a simple food?
            </span>
          </li>
        </ul>
        <p className="text-slate-700 mt-6 mb-0" data-id="element-160">
          These questions often reveal more than any marketing slogan.
        </p>
      </div>

      <div className="mb-16" data-id="element-161">
        <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-162">
          How IntelliEats Helps
        </h2>
        <p className="text-slate-700 mb-4" data-id="element-163">
          Reading ingredient labels can be time-consuming, especially when
          ingredient names are unfamiliar or difficult to pronounce.
        </p>
        <p className="text-slate-700 mb-4" data-id="element-164">
          IntelliEats simplifies the process by allowing users to scan
          ingredient lists directly from food packaging. The app highlights
          ingredients that may be worth limiting or avoiding and provides
          easy-to-understand explanations, helping shoppers make informed
          decisions in seconds.
        </p>
        <p className="text-slate-700 font-medium" data-id="element-165">
          Because healthier choices start with understanding what's really
          inside the package.
        </p>
      </div>

      <div className="mb-16" data-id="element-166">
        <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-167">
          Final Thought
        </h2>
        <p className="text-slate-700 mb-4" data-id="element-168">
          Food companies are experts at marketing. As consumers, our best
          defense is knowledge.
        </p>
        <p className="text-slate-700 mb-4" data-id="element-169">
          The next time a package promises "healthy," "natural," or
          "sugar-free," take a closer look. The ingredient list often tells a
          very different story.
        </p>
        <p className="text-slate-700" data-id="element-170">
          A few extra seconds spent checking ingredients today can lead to
          healthier habits for years to come.
        </p>
      </div>

      {/* References */}
      <div className="border-t border-slate-200 pt-8" data-id="element-171">
        <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-172">References</h2>
        <ul className="space-y-2 text-sm text-slate-600 list-disc pl-5" data-id="element-173">
          <li data-id="element-174">
            U.S. Food &amp; Drug Administration (FDA). "How to Understand and
            Use the Nutrition Facts Label."{' '}
            <a href="https://www.fda.gov/food/nutrition-facts-label" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-175">
              fda.gov/food/nutrition-facts-label
            </a>
          </li>
          <li data-id="element-176">
            U.S. Food &amp; Drug Administration (FDA). "Food Labeling &amp;
            Nutrition."{' '}
            <a href="https://www.fda.gov/food/food-labeling-nutrition" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-177">
              fda.gov/food/food-labeling-nutrition
            </a>
          </li>
          <li data-id="element-178">
            U.S. Department of Agriculture (USDA). "Dietary Guidelines for
            Americans."{' '}
            <a href="https://www.dietaryguidelines.gov" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-179">
              dietaryguidelines.gov
            </a>
          </li>
          <li data-id="element-180">
            Harvard T.H. Chan School of Public Health. "The Nutrition Source."{' '}
            <a href="https://nutritionsource.hsph.harvard.edu" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-181">
              nutritionsource.hsph.harvard.edu
            </a>
          </li>
          <li data-id="element-182">
            Whole Grains Council. "Identifying Whole Grain Products."{' '}
            <a href="https://wholegrainscouncil.org" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-183">
              wholegrainscouncil.org
            </a>
          </li>
          <li data-id="element-184">
            American Heart Association. "Added Sugars."{' '}
            <a href="https://www.heart.org" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-185">
              heart.org
            </a>
          </li>
          <li data-id="element-186">
            Academy of Nutrition and Dietetics. "Understanding Food Labels."{' '}
            <a href="https://www.eatright.org" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-187">
              eatright.org
            </a>
          </li>
          <li data-id="element-188">
            Mayo Clinic. "Nutrition and Healthy Eating."{' '}
            <a href="https://www.mayoclinic.org/healthy-lifestyle/nutrition-and-healthy-eating" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-189">
              mayoclinic.org
            </a>
          </li>
          <li data-id="element-190">
            Centers for Disease Control and Prevention (CDC). "Healthy Weight,
            Nutrition, and Physical Activity."{' '}
            <a href="https://www.cdc.gov/healthy-weight-growth" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-191">
              cdc.gov/healthy-weight-growth
            </a>
          </li>
          <li data-id="element-192">
            National Institutes of Health (NIH). "Nutrition Research and
            Consumer Health Information."{' '}
            <a href="https://www.nih.gov/health-information" target="_blank" rel="noopener noreferrer" className="text-brand-600 break-words" data-id="element-193">
              nih.gov/health-information
            </a>
          </li>
        </ul>
      </div>
    </div>;
}
export function getPostBody(slug: string) {
  switch (slug) {
    case 'beyond-the-calorie-count':
      return <BeyondCalorieCountBody data-id="element-194" />;
    case 'healthy-food-trap':
      return <HealthyFoodTrapBody data-id="element-195" />;
    default:
      return null;
  }
}